import React from 'react';
import type { IconType } from 'react-icons';
import { LuAlertTriangle } from 'react-icons/lu';

import MergeClassNames from 'utils/MergeClassNames';

type NoticeSize = 'sm' | 'md';

const TEXT_CLASS: Record<NoticeSize, string> = {
  sm: 'text-sm',
  md: 'text-base',
};

interface NoticeProps {
  /** The message. */
  children: React.ReactNode;
  icon?: IconType;
  /** Buttons under the message, lined up with its text. */
  actions?: React.ReactNode;
  size?: NoticeSize;
  role?: 'status' | 'note';
  /** Layout only: margins, width, scrolling. */
  className?: string;
}

/**
 * An informational banner (design system 3.4): dark text on the information
 * ground, marked by its icon and words rather than a status colour.
 */
const Notice: React.FC<NoticeProps> = ({
  children,
  icon: Icon = LuAlertTriangle,
  actions,
  size = 'sm',
  role = 'status',
  className,
}) => (
  <div
    role={role}
    className={MergeClassNames(
      'flex flex-col gap-2 rounded-md bg-information px-3 py-2 text-charcoal',
      TEXT_CLASS[size],
      className
    )}
  >
    <span className="flex items-start gap-2">
      <Icon
        size={16}
        aria-hidden
        className="mt-0.5 flex-shrink-0 text-charcoal"
      />
      <span className="min-w-0 break-words">{children}</span>
    </span>
    {/* Indented past the icon and its gap, to line up with the message. */}
    {actions && (
      <span className="flex flex-wrap items-center gap-2 pl-6">{actions}</span>
    )}
  </div>
);

export default Notice;
